/**
 * React hooks for Analyzify SDK
 */

import { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { AnalyzifyContext, type AnalyzifyContextValue } from './provider';
import type { StorefrontProduct, StorefrontCart, StorefrontOrder } from '../types/storefront';
import type { PageContext } from '../types/config';
import type { ConsentState } from '../types/events';

/**
 * Get the Analyzify context
 */
export function useAnalyzify(): AnalyzifyContextValue {
  const context = useContext(AnalyzifyContext);

  if (!context) {
    throw new Error('useAnalyzify must be used within an AnalyzifyProvider');
  }

  return context;
}

/**
 * Hook to track if Analyzify is ready
 */
export function useAnalyzifyReady(): boolean {
  const { isReady } = useAnalyzify();
  return isReady;
}

/**
 * Hook to automatically track events when dependencies change
 *
 * @example
 * ```tsx
 * // Track view_item when product changes
 * useTrackEvent('viewItem', [product], () => {
 *   return product ? [product] : null;
 * });
 * ```
 */
export function useTrackEvent<T extends keyof AnalyzifyContextValue['track']>(
  eventName: T,
  deps: any[],
  getArgs: () => Parameters<AnalyzifyContextValue['track'][T]> | null
): void {
  const { track, isReady } = useAnalyzify();
  const hasTracked = useRef(false);

  useEffect(() => {
    if (!isReady) return;

    const args = getArgs();
    if (!args) return;

    // Prevent double tracking
    if (hasTracked.current) return;
    hasTracked.current = true;

    // Call the track method with error handling
    try {
      const trackFn = track[eventName] as (...args: any[]) => void;
      trackFn(...args);
    } catch (error) {
      console.error(`[Analyzify] Failed to track ${eventName}:`, error);
    }

    // Reset on deps change
    return () => {
      hasTracked.current = false;
    };
  }, [isReady, ...deps]);
}

/**
 * Hook to set page context on mount
 */
export function usePageContext(context: PageContext | null): void {
  const { setPageContext, isReady } = useAnalyzify();
  // Track which context has been set to prevent duplicates in Strict Mode
  const setContextType = useRef<string | null>(null);

  useEffect(() => {
    if (!isReady || !context) return;
    // Create a stable key for this context
    const contextKey = `${context.type}-${(context as any).product?.id || (context as any).collection?.id || (context as any).cart?.id || 'default'}`;
    if (setContextType.current === contextKey) return;

    setContextType.current = contextKey;
    setPageContext(context);

    // NOTE: Do NOT reset in cleanup - this prevents double-setting in React Strict Mode
  }, [isReady, context]);
}

/**
 * Hook to track view_item on product pages
 */
export function useTrackViewItem(
  product: StorefrontProduct | null,
  variantId?: string
): void {
  const { track, isReady } = useAnalyzify();
  const trackedId = useRef<string | null>(null);

  useEffect(() => {
    if (!isReady || !product) return;

    // Track only once per product/variant combination
    const trackingId = `${product.id}-${variantId || 'default'}`;
    if (trackedId.current === trackingId) return;

    trackedId.current = trackingId;
    try {
      track.viewItem(product, variantId);
    } catch (error) {
      console.error('[Analyzify] Failed to track view_item:', error);
    }
  }, [isReady, product?.id, variantId]);
}

/**
 * Hook to track view_item_list on collection pages
 */
export function useTrackViewItemList(
  products: StorefrontProduct[] | null,
  listName: string,
  listId?: string
): void {
  const { track, isReady } = useAnalyzify();
  // Track which list has been tracked to prevent duplicates in Strict Mode
  const trackedListKey = useRef<string | null>(null);

  useEffect(() => {
    if (!isReady || !products || products.length === 0) return;
    const listKey = `${listName}-${listId || 'default'}`;
    if (trackedListKey.current === listKey) return;

    trackedListKey.current = listKey;
    try {
      track.viewItemList(products, listName, listId);
    } catch (error) {
      console.error('[Analyzify] Failed to track view_item_list:', error);
    }

    // NOTE: Do NOT reset in cleanup - this prevents double-tracking in React Strict Mode
  }, [isReady, listName, listId]);
}

/**
 * Keep the SDK informed of the current cart.
 *
 * Call once where your app holds its cart (e.g. next to your cart provider).
 * The SDK writes the cart token to the `cart` cookie and
 * window.analyzify.cart_id, so the CDN scripts can attach the visit's
 * attribution to the cart (see AnalyzifyOptions.cartAttributes).
 *
 * @example
 * ```tsx
 * const { cart } = useCart();
 * useCartSync(cart);
 * ```
 */
export function useCartSync(cart: StorefrontCart | null | undefined): void {
  const { setCart } = useAnalyzify();
  const cartKey = cart ? `${cart.id}|${cart.totalQuantity}|${cart.updatedAt ?? ''}` : '';
  // A cart that was known and is now gone (completed checkout, reset) is
  // cleared; a cart that is still loading (null from the start) is not.
  const hadCart = useRef(false);

  useEffect(() => {
    if (cart) {
      hadCart.current = true;
      setCart(cart);
    } else if (hadCart.current) {
      hadCart.current = false;
      setCart(null);
    }
    // Re-sync when the cart changes (cartKey), not on every new cart object
  }, [setCart, cartKey]);
}

/**
 * Hook to track view_cart on cart pages
 */
export function useTrackViewCart(cart: StorefrontCart | null): void {
  const { track, isReady } = useAnalyzify();
  // Track which cart has been tracked to prevent duplicates in Strict Mode
  const trackedCartId = useRef<string | null>(null);

  useEffect(() => {
    if (!isReady || !cart) return;
    if (trackedCartId.current === cart.id) return;

    trackedCartId.current = cart.id;
    try {
      track.viewCart(cart);
    } catch (error) {
      console.error('[Analyzify] Failed to track view_cart:', error);
    }

    // NOTE: Do NOT reset in cleanup - this prevents double-tracking in React Strict Mode
  }, [isReady, cart?.id]);
}

/**
 * Hook to track search events
 */
export function useTrackSearch(
  term: string | null,
  results: StorefrontProduct[] | null,
  resultsCount: number
): void {
  const { track, isReady } = useAnalyzify();
  const trackedTerm = useRef<string | null>(null);

  useEffect(() => {
    if (!isReady || !term || !results) return;
    if (trackedTerm.current === term) return;

    trackedTerm.current = term;
    try {
      track.search(term, results, resultsCount);
    } catch (error) {
      console.error('[Analyzify] Failed to track search:', error);
    }
  }, [isReady, term]);
}

// ═══════════════════════════════════════════════════════════════
// NEW HOOKS FOR NEXT.JS APP ROUTER
// ═══════════════════════════════════════════════════════════════

/**
 * Hook to access and manage consent state
 *
 * @example
 * ```tsx
 * const { consent, setConsent, acceptAll, rejectAll, canTrack } = useConsent();
 *
 * // Check current consent
 * if (consent.analytics) {
 *   // Analytics allowed
 * }
 *
 * // Update consent
 * setConsent({ analytics: true, marketing: false });
 *
 * // Quick actions
 * acceptAll();  // Enable all consent types
 * rejectAll();  // Disable all consent types
 *
 * // Check if tracking is allowed
 * if (canTrack()) {
 *   // Track event
 * }
 * ```
 */
export function useConsent() {
  const { consent, isReady } = useAnalyzify();
  const [consentState, setConsentState] = useState<ConsentState>(() => consent.getConsent());

  // Sync state when consent changes externally
  useEffect(() => {
    if (!isReady) return;
    setConsentState(consent.getConsent());
  }, [isReady]);

  const setConsent = useCallback(
    (state: Partial<ConsentState>) => {
      consent.setConsent(state);
      setConsentState(consent.getConsent());
    },
    [consent]
  );

  const acceptAll = useCallback(() => {
    setConsent({ analytics: true, marketing: true, preferences: true });
  }, [setConsent]);

  const rejectAll = useCallback(() => {
    setConsent({ analytics: false, marketing: false, preferences: false });
  }, [setConsent]);

  return {
    consent: consentState,
    setConsent,
    acceptAll,
    rejectAll,
    canTrack: consent.canTrack,
  };
}

/**
 * Hook to track route changes in Next.js App Router
 *
 * This hook should be used once at the app root level to automatically
 * track page views when the route changes.
 *
 * @example
 * ```tsx
 * // In your root layout or a client component near root
 * 'use client';
 *
 * import { useRouteTracking } from 'analyzify-js/react';
 *
 * export function RouteTracker() {
 *   useRouteTracking();
 *   return null;
 * }
 * ```
 */
export function useRouteTracking(): void {
  const { setPageContext, isReady } = useAnalyzify();
  const previousPath = useRef<string | null>(null);
  const isSetupRef = useRef(false);
  const originalMethodsRef = useRef<{
    pushState: typeof history.pushState;
    replaceState: typeof history.replaceState;
  } | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !isReady) return;
    if (isSetupRef.current) return;
    isSetupRef.current = true;

    // Store originals before patching
    originalMethodsRef.current = {
      pushState: history.pushState.bind(history),
      replaceState: history.replaceState.bind(history),
    };

    // Track initial page
    const currentPath = window.location.pathname;
    if (previousPath.current !== currentPath) {
      previousPath.current = currentPath;

      // Determine page type from pathname
      const pageType = getPageTypeFromPath(currentPath);
      setPageContext({ type: pageType, provisional: true });
    }

    // Listen for route changes (Next.js uses popstate and pushstate)
    const handleRouteChange = () => {
      const newPath = window.location.pathname;
      if (previousPath.current !== newPath) {
        previousPath.current = newPath;
        const pageType = getPageTypeFromPath(newPath);
        setPageContext({ type: pageType, provisional: true });
      }
    };

    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', handleRouteChange);

    // Create wrapped methods with marker to identify our patches
    const wrappedPush = function (...args: Parameters<typeof history.pushState>) {
      originalMethodsRef.current?.pushState(...args);
      handleRouteChange();
    };
    (wrappedPush as any).__analyzify__ = true;

    const wrappedReplace = function (...args: Parameters<typeof history.replaceState>) {
      originalMethodsRef.current?.replaceState(...args);
      handleRouteChange();
    };
    (wrappedReplace as any).__analyzify__ = true;

    history.pushState = wrappedPush;
    history.replaceState = wrappedReplace;

    return () => {
      isSetupRef.current = false;
      window.removeEventListener('popstate', handleRouteChange);

      // Only restore if we still own these patches
      if (originalMethodsRef.current) {
        if ((history.pushState as any).__analyzify__) {
          history.pushState = originalMethodsRef.current.pushState;
        }
        if ((history.replaceState as any).__analyzify__) {
          history.replaceState = originalMethodsRef.current.replaceState;
        }
      }
    };
  }, [isReady, setPageContext]);
}

/**
 * Helper to determine page type from pathname
 */
function getPageTypeFromPath(
  pathname: string
): 'product' | 'collection' | 'search' | 'cart' | 'index' | 'page' | 'other' {
  // Tolerate a locale prefix (/en, /en-gb) and Shopify's collection-scoped
  // product URLs (/collections/x/products/y)
  const path = pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, '') || '/';
  if (path === '/' || path === '/index') return 'index';
  if (path.includes('/products/')) return 'product';
  if (path.startsWith('/collections/')) return 'collection';
  if (path.startsWith('/search')) return 'search';
  if (path.startsWith('/cart')) return 'cart';
  if (path.startsWith('/pages/')) return 'page';
  return 'other';
}

/**
 * Hook to track homepage views
 *
 * @example
 * ```tsx
 * // In your homepage component
 * export default function HomePage() {
 *   useTrackHomepage();
 *   return <div>...</div>;
 * }
 * ```
 */
export function useTrackHomepage(): void {
  const { track, setPageContext, isReady } = useAnalyzify();
  // Use boolean ref but don't reset in cleanup to prevent double-tracking in Strict Mode
  const hasTracked = useRef(false);

  useEffect(() => {
    if (!isReady || hasTracked.current) return;

    hasTracked.current = true;
    try {
      setPageContext({ type: 'index' });
      track.homepage();
    } catch (error) {
      console.error('[Analyzify] Failed to track homepage:', error);
    }

    // NOTE: Do NOT reset hasTracked in cleanup - this prevents double-tracking in React Strict Mode
  }, [isReady]);
}

/**
 * Hook to track purchase events (for thank you / order confirmation pages)
 *
 * @example
 * ```tsx
 * // In your checkout success page
 * export default function CheckoutSuccess({ order }) {
 *   useTrackPurchase(order);
 *   return <div>Thank you for your order!</div>;
 * }
 * ```
 */
export function useTrackPurchase(order: StorefrontOrder | null): void {
  const { track, setPageContext, isReady } = useAnalyzify();
  const trackedOrderId = useRef<string | null>(null);

  useEffect(() => {
    if (!isReady || !order) return;
    if (trackedOrderId.current === order.id) return;

    trackedOrderId.current = order.id;
    try {
      setPageContext({ type: 'other' }); // Thank you page is 'other' type
      track.purchase(order);
    } catch (error) {
      console.error('[Analyzify] Failed to track purchase:', error);
    }
  }, [isReady, order?.id]);
}

/**
 * Hook to get a callback for begin checkout tracking
 * Returns a function you can call when user clicks checkout button
 *
 * @example
 * ```tsx
 * const beginCheckout = useTrackBeginCheckout(cart);
 *
 * <button onClick={() => {
 *   beginCheckout();
 *   router.push('/checkout');
 * }}>
 *   Checkout
 * </button>
 * ```
 */
export function useTrackBeginCheckout(cart: StorefrontCart | null): () => void {
  const { track, isReady } = useAnalyzify();

  return useCallback(() => {
    if (!isReady || !cart) return;
    try {
      track.beginCheckout(cart);
    } catch (error) {
      console.error('[Analyzify] Failed to track begin_checkout:', error);
    }
  }, [isReady, cart, track]);
}
